import React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import { useAuth } from '../context/AuthContextBackend'
import { toast } from 'sonner'
import { Lock } from '@phosphor-icons/react'

/** Map module IDs to user-facing display names */
const MODULE_DISPLAY_NAMES: Record<string, string> = {
  'perform-spark': 'PerformSpark',
  'comp-builder': 'CompBldr',
  'learn': 'TraineryLearn',
}

/** Format an ISO date string to readable English, e.g. "1 March 2026" */
const formatEndDate = (iso: string): string => {
  const date = new Date(iso)
  if (isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

interface SubscriptionGuardProps {
  moduleId: string
  children: React.ReactNode
}

/**
 * Route-level guard that controls access to module routes based on subscription status.
 *
 * - Module expired (is_active: false): Renders children blurred with an overlay message
 * - Module not enabled (is_enabled: false): Redirects to /home/my-portal with toast
 * - Customer employees bypass this check — their access is controlled
 *   by account.access (handled in the sidebar via customerModuleAccess), not subscriptions.
 */
export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ moduleId, children }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { isModuleEnabled, isModuleActive, getModuleName, getModuleEndDate, isLoading, subscriptions } = useSubscriptionStore()

  // Check if user has module access via account.access array (for customer accounts)
  const accountAccess: string[] = (user as any)?.access || []
  const hasAccountModuleAccess = accountAccess.length > 0

  const moduleExpired = !hasAccountModuleAccess && !isLoading && subscriptions.length > 0 && !isModuleActive(moduleId)
  const shouldBlock = !hasAccountModuleAccess && !isLoading && subscriptions.length > 0 && !isModuleEnabled(moduleId)

  // While subscriptions are still loading, don't block
  if (isLoading) {
    return null
  }

  // Module not enabled at all — redirect with toast
  if (shouldBlock && !moduleExpired) {
    toast.error('This module is not available for your account. Please contact your administrator.')
    return <Navigate to="/home/my-portal" replace />
  }

  // Module expired (is_active: false) — show children blurred with overlay
  if (moduleExpired) {
    const displayName = MODULE_DISPLAY_NAMES[moduleId] || getModuleName(moduleId) || 'Module'
    const endDate = getModuleEndDate(moduleId)

    return (
      <div className="relative h-full w-full">
        {/* Blurred content behind overlay */}
        <div className="pointer-events-none select-none blur-sm opacity-40 h-full w-full">
          {children}
        </div>

        {/* Overlay with message box */}
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/5 backdrop-blur-[2px]">
          <div className="flex flex-col items-center gap-4 px-8 py-8 rounded-2xl border border-gray-200 bg-white shadow-lg max-w-md text-center">
            <div
              className="flex items-center justify-center w-14 h-14 rounded-full"
              style={{ backgroundColor: '#FEE2E2' }}
            >
              <Lock className="w-7 h-7 text-red-500" weight="fill" />
            </div>
            <div>
              <h2
                className="text-lg font-semibold text-gray-900"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                Subscription Expired
              </h2>
              <p
                className="mt-2 text-sm text-gray-500"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                Your subscription for the <span className="font-semibold text-gray-700">{displayName}</span> module has expired
                {endDate ? ` on ${formatEndDate(endDate)}` : ''}.
                Please contact your administrator to renew.
              </p>
            </div>
            <button
              onClick={() => navigate('/home/my-portal')}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
              style={{
                backgroundColor: '#4B45E5',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default SubscriptionGuard
